import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { readLastWorkspaceSearch } from "../workspace-last-search";
import { WorkspacePools } from "../workspace-pools";
import {
  psvStatusLabel,
  psvStatusTextClassName,
} from "../workspace-psv-presentation";
import type { WorkspaceProvider } from "../workspace-types";
import { cn } from "@/lib/utils";

const GRID_ROUTE = "/org/bu/$organizationBusinessUnitId/providers/credentials";
const LINKED_ACCOUNT_EXPLANATION =
  "Yes means this provider record is connected to a Saile login. Manually added records without a login show No.";

export type ProviderRunFacts = {
  npi: string | null;
  additionalNames: string[];
  licensedStates: string[];
};

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-1.5 text-sm">
      <dt className="text-muted-foreground">{label}:</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

export function ProviderPageHeader({
  provider,
  organizationBusinessUnitId,
  businessUnitName,
  tabLabel,
  runFacts,
  verificationStatus,
  runVerification,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
  businessUnitName?: string;
  tabLabel: string;
  runFacts?: ProviderRunFacts;
  /** Shown beside the business unit when verification is available. */
  verificationStatus?: NonNullable<WorkspaceProvider["psvStatus"]>;
  /** The page's Run verification control; absent without that access.
   * `hasRun` words it as a new run once one exists. */
  runVerification?: { disabled: boolean; hasRun: boolean; onRun: () => void };
}) {
  const name = provider.name || "Name not provided";
  const npi = provider.npi ?? runFacts?.npi;
  const roleLine = [provider.role, provider.specialty]
    .filter(Boolean)
    .join(" · ");
  const metaParts = [roleLine, businessUnitName].filter(Boolean);
  return (
    <header className="space-y-4">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                to={GRID_ROUTE}
                params={{ organizationBusinessUnitId }}
                search={readLastWorkspaceSearch(organizationBusinessUnitId)}
              >
                Provider credentials
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {name} · {tabLabel}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <h1 className="break-words text-3xl font-semibold tracking-tight">
            {name}
          </h1>
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            {metaParts.map((part, index) => (
              <span key={part} className={cn(index > 0 && "border-l pl-2")}>
                {part}
              </span>
            ))}
            {verificationStatus && (
              <span
                className={cn(
                  "border-l pl-2 font-medium",
                  psvStatusTextClassName(verificationStatus.status),
                )}
              >
                {psvStatusLabel(verificationStatus)}
              </span>
            )}
          </p>
        </div>
        {runVerification && (
          <Button
            variant="surface"
            disabled={runVerification.disabled}
            onClick={runVerification.onRun}
          >
            <Play className="mr-2 size-4" aria-hidden />
            {runVerification.hasRun
              ? "Run new verification"
              : "Run verification"}
          </Button>
        )}
      </div>
      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        {npi && <Fact label="NPI">{npi}</Fact>}
        {runFacts && runFacts.licensedStates.length > 0 && (
          <Fact label="Licensed states">
            {runFacts.licensedStates.join(", ")}
          </Fact>
        )}
        <Fact label="Saile account">
          <span title={LINKED_ACCOUNT_EXPLANATION}>
            {provider.linked ? "Yes" : "No"}
          </span>
        </Fact>
        {provider.capabilities.canViewPools && (
          <Fact label="Pools">
            <WorkspacePools
              provider={provider}
              organizationBusinessUnitId={organizationBusinessUnitId}
            />
          </Fact>
        )}
      </dl>
      {runFacts && runFacts.additionalNames.length > 0 && (
        <p className="break-words text-xs leading-5 text-muted-foreground">
          Other recorded names: {runFacts.additionalNames.join("; ")}
        </p>
      )}
    </header>
  );
}
